import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { CheckCircle2, CircleStop, Plus, Upload } from 'lucide-react';
import { type ChangeEvent, type DragEvent, type ReactNode, useMemo, useRef, useState } from 'react';

import type { DevelopmentDetailAiAnalysisResponse } from '@/domain/models/development.js';
import type { AiChatAttachmentRef, AiChatRepository } from '@/domain/repositories/ai-chat-repository.js';
import { ConfirmDialog } from '@/presentation/components/index.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useEfChat } from '@/presentation/hooks/use-ef-chat.js';
import { EfChatComposer } from '@/presentation/pages/developments/chat/ef-chat-composer.js';
import { EfChatEfAttachment } from '@/presentation/pages/developments/chat/ef-chat-ef-attachment.js';
import { EfChatElicitationPrompt } from '@/presentation/pages/developments/chat/ef-chat-elicitation.js';
import { EfChatMessage } from '@/presentation/pages/developments/chat/ef-chat-message.js';
import { EfContextUsageIndicator } from '@/presentation/pages/developments/chat/ef-context-usage-indicator.js';
import { EfModelSelector } from '@/presentation/pages/developments/chat/ef-model-selector.js';
import { MAX_CHAT_ATTACHMENTS, MAX_CHAT_ATTACHMENT_SIZE_BYTES, isAllowedChatAttachment, splitDroppedFiles } from '@/presentation/pages/developments/chat/utils/attachment-utils.js';

type PendingAttachment = {
    id: string;
    fileName: string;
    fileSizeBytes: number;
    file: File;
};

type TimelineItem =
    | { kind: 'analysis'; id: string; at: number; analysis: DevelopmentDetailAiAnalysisResponse }
    | {
          kind: 'message';
          id: string;
          at: number;
          message: { id: string; role: 'user' | 'assistant'; content: string; reasoning?: string; status: 'streaming' | 'complete' | 'error' };
      };

export type EfChatAttachmentView = {
    id: string;
    fileName: string;
    metaLabel?: string;
    onDownload?: () => void;
    canDelete?: boolean;
};

export type EfChatPanelProps = {
    developmentId: string;
    aiChatRepository: AiChatRepository;
    analyses: DevelopmentDetailAiAnalysisResponse[];
    renderAnalysis: (analysis: DevelopmentDetailAiAnalysisResponse) => ReactNode;
    onVerdict?: () => void;
    isCf?: boolean;
    isAnalysisInProgress?: boolean;
    efPendingEvaluation?: boolean;
    chatUnlocked?: boolean;
    canApprove?: boolean;
    efAttachments?: EfChatAttachmentView[];
    onUploadEf?: (file: File) => void;
    onApproveEf?: () => void;
    onDeleteEf?: () => void;
    acceptedFormats?: string;
    footerSlot?: ReactNode;
};

export function EfChatPanel({
    developmentId,
    aiChatRepository,
    analyses,
    renderAnalysis,
    onVerdict,
    isCf = false,
    isAnalysisInProgress = false,
    efPendingEvaluation = false,
    chatUnlocked = false,
    canApprove = false,
    efAttachments = [],
    onUploadEf,
    onApproveEf,
    onDeleteEf,
    acceptedFormats = '.docx,.doc,.pdf,.xlsx',
    footerSlot
}: EfChatPanelProps) {
    const { translate, useAuthStore } = useApp();
    const isGlobalAdmin = useAuthStore((s) => s.user?.isGlobalAdmin);
    const chat = useEfChat(developmentId, aiChatRepository, { onVerdict });
    const inProgress = isAnalysisInProgress || chat.isStreaming;
    const evaluateMode = isCf && efPendingEvaluation && !inProgress;
    const chatEnabled = chatUnlocked && chat.canSend && !inProgress && !evaluateMode;
    const composerDisabled = inProgress || (!evaluateMode && !chatEnabled);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [dragging, setDragging] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
    const [attachError, setAttachError] = useState<string | null>(null);
    const [sendingAttachments, setSendingAttachments] = useState(false);
    const canUpload = isCf && Boolean(onUploadEf);
    const errorMessages = useMemo<Record<string, string>>(
        () => ({
            contentFilter: translate('efChat.errors.contentFilter'),
            modelNotAllowed: translate('efChat.errors.modelNotAllowed'),
            connection: translate('efChat.errors.connection'),
            interrupted: translate('efChat.errors.interrupted'),
            auth: translate('efChat.errors.auth'),
            modelNotFound: translate('efChat.errors.modelNotFound'),
            rateLimit: translate('efChat.errors.rateLimit'),
            timeout: translate('efChat.errors.timeout'),
            sap: translate('efChat.errors.sap'),
            default: translate('efChat.errors.default')
        }),
        [translate]
    );
    const attachmentsEnabled = isCf && !inProgress && (chatEnabled || evaluateMode) && Boolean(aiChatRepository);

    const handleAttachFiles = (files: File[]): void => {
        const errors: string[] = [];
        const additions: PendingAttachment[] = [];
        for (const file of files) {
            if (pendingAttachments.length + additions.length >= MAX_CHAT_ATTACHMENTS) {
                errors.push(translate('efChat.attachmentTooMany'));
                break;
            }
            if (file.size > MAX_CHAT_ATTACHMENT_SIZE_BYTES) {
                errors.push(translate('efChat.attachmentTooLarge', { name: file.name }));
                continue;
            }
            if (!isAllowedChatAttachment(file.name)) {
                errors.push(translate('efChat.attachmentTypeNotAllowed', { name: file.name }));
                continue;
            }
            additions.push({ id: crypto.randomUUID(), fileName: file.name, fileSizeBytes: file.size, file });
        }
        if (additions.length > 0) {
            setPendingAttachments((current) => [...current, ...additions]);
        }
        setAttachError(errors.length > 0 ? errors.join(' ') : null);
    };

    const removePendingAttachment = (id: string): void => {
        setPendingAttachments((current) => {
            const remaining = current.filter((attachment) => attachment.id !== id);
            if (remaining.length === 0) {
                setAttachError(null);
            }
            return remaining;
        });
    };

    const submitEfFile = (file: File): void => {
        if (!onUploadEf) {
            return;
        }
        void (async () => {
            if (inProgress) {
                await chat.cancel();
            }
            onUploadEf(file);
        })();
    };

    const confirmDeleteEf = (): void => {
        if (!onDeleteEf || inProgress) {
            return;
        }
        onDeleteEf();
        setDeleteConfirmOpen(false);
    };

    const timeline = useMemo((): TimelineItem[] => {
        const items: TimelineItem[] = [
            ...analyses.map((analysis) => ({
                kind: 'analysis' as const,
                id: `analysis-${analysis.id}`,
                at: new Date(analysis.analyzedAt).getTime(),
                analysis
            })),
            ...chat.messages.map((message) => ({
                kind: 'message' as const,
                id: `message-${message.id}`,
                at: new Date(message.createdAt).getTime(),
                message
            }))
        ];
        return items.sort((a, b) => a.at - b.at);
    }, [analyses, chat.messages]);

    let composerReasonText: string | null = null;
    if (!evaluateMode && !chatEnabled && !inProgress) {
        if (isCf && efAttachments.length === 0) {
            composerReasonText = translate('efChat.attachEfHint');
        } else if (efPendingEvaluation) {
            composerReasonText = translate('efChat.chatLockedUntilEvaluated');
        } else {
            composerReasonText = translate('efChat.readOnlyStatus');
        }
    }

    const uploadPendingAttachments = async (): Promise<AiChatAttachmentRef[] | null> => {
        try {
            return await Promise.all(pendingAttachments.map((attachment) => aiChatRepository.uploadChatAttachment(developmentId, attachment.file)));
        } catch {
            setAttachError(translate('efChat.attachmentUploadFailed'));
            return null;
        }
    };

    const handleComposerSend = (content: string): void => {
        if (evaluateMode) {
            if (pendingAttachments.length === 0) {
                void chat.startAnalysis(content || undefined);
                return;
            }
            setSendingAttachments(true);
            void (async () => {
                const refs = await uploadPendingAttachments();
                if (refs) {
                    setPendingAttachments([]);
                    setAttachError(null);
                    void chat.startAnalysis(content || undefined, refs);
                }
                setSendingAttachments(false);
            })();
            return;
        }
        if (pendingAttachments.length > 0) {
            setSendingAttachments(true);
            void (async () => {
                const refs = await uploadPendingAttachments();
                if (!refs) {
                    setSendingAttachments(false);
                    return;
                }
                try {
                    await chat.send(content, refs);
                    setPendingAttachments([]);
                    setAttachError(null);
                } catch {
                    // erro de envio já é exibido via chat.error (setError dentro de useEfChat.send)
                } finally {
                    setSendingAttachments(false);
                }
            })();
            return;
        }
        // erro de envio já é exibido via chat.error (setError dentro de useEfChat.send)
        void chat.send(content).catch(() => {});
    };

    const handleDownloadAttachment = (fileUrl: string): void => {
        const win = window.open('', '_blank', 'noopener,noreferrer');
        void (async () => {
            try {
                const { url } = await aiChatRepository.getChatAttachmentDownloadUrl(developmentId, fileUrl);
                if (win) {
                    win.location.href = url;
                } else {
                    window.location.href = url;
                }
            } catch {
                win?.close();
                setAttachError(translate('efChat.attachmentDownloadFailed'));
            }
        })();
    };

    const handleFilePick = (event: ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (file) {
            submitEfFile(file);
        }
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
        if (!canUpload) {
            return;
        }
        event.preventDefault();
        setDragging(true);
    };

    const handleDragLeave = (event: DragEvent<HTMLDivElement>): void => {
        if (event.currentTarget.contains(event.relatedTarget as Node)) {
            return;
        }
        setDragging(false);
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
        if (!canUpload) {
            return;
        }
        event.preventDefault();
        setDragging(false);
        const { efFile, complementaryFiles } = splitDroppedFiles(Array.from(event.dataTransfer.files ?? []));
        if (!efFile) {
            return;
        }
        submitEfFile(efFile);
        if (complementaryFiles.length > 0) {
            handleAttachFiles(complementaryFiles);
        }
    };

    const cancelButton =
        isCf && inProgress ? (
            <Tooltip title={translate('efChat.cancelAnalysis')}>
                <IconButton
                    size="small"
                    color="error"
                    aria-label={translate('efChat.cancelAnalysis')}
                    onClick={() => {
                        void chat.cancel();
                    }}
                >
                    <CircleStop size={18} />
                </IconButton>
            </Tooltip>
        ) : null;

    const approveButton =
        canApprove && !inProgress && onApproveEf ? (
            <Button size="small" variant="contained" color="success" startIcon={<CheckCircle2 size={16} />} onClick={() => onApproveEf()}>
                {translate('efChat.approveEf')}
            </Button>
        ) : null;

    const attachmentSlot =
        efAttachments.length > 0 ? (
            <Stack spacing={1} data-testid="ef-chat-attachments">
                {efAttachments.map((attachment) => (
                    <EfChatEfAttachment
                        key={attachment.id}
                        fileName={attachment.fileName}
                        metaLabel={attachment.metaLabel}
                        onDownload={attachment.onDownload}
                        downloadLabel={translate('documents.download')}
                        onDelete={attachment.canDelete && onDeleteEf && !inProgress ? () => setDeleteConfirmOpen(true) : undefined}
                        deleteLabel={translate('efChat.deleteEf')}
                    />
                ))}
            </Stack>
        ) : null;

    const leftSlot =
        isCf && onUploadEf ? (
            <Tooltip title={efAttachments.length > 0 ? translate('efChat.replaceEf') : translate('efChat.attachEf')}>
                <IconButton size="small" onClick={() => fileInputRef.current?.click()} aria-label={translate('efChat.attachEf')}>
                    <Plus size={18} />
                </IconButton>
            </Tooltip>
        ) : null;

    return (
        <Box
            data-testid="ef-chat-panel"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
                position: 'relative',
                width: '100%',
                maxWidth: 900,
                mx: 'auto',
                alignSelf: 'center',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0
            }}
        >
            {dragging ? (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 5,
                        borderRadius: 3,
                        border: '2px dashed',
                        borderColor: 'primary.main',
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        pointerEvents: 'none'
                    }}
                >
                    <Upload size={28} style={{ opacity: 0.7 }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'text.secondary' }}>{translate('efChat.dropHint')}</Typography>
                </Box>
            ) : null}
            <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
                {chat.error ? <Alert severity="error">{chat.error}</Alert> : null}
                <input ref={fileInputRef} type="file" accept={acceptedFormats} onChange={handleFilePick} style={{ display: 'none' }} data-testid="ef-chat-file-input" />
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, px: { xs: 0, sm: 4 } }}>
                    {timeline.length === 0 ? (
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, py: 6 }}>
                            <Typography sx={{ fontSize: 22, fontWeight: 500, color: 'text.primary', textAlign: 'center' }}>{translate('efChat.greeting')}</Typography>
                            {attachmentSlot}
                            {efAttachments.length === 0 && isCf ? (
                                <Typography sx={{ fontSize: 13, color: 'text.disabled', textAlign: 'center' }}>{translate('efChat.attachEfHint')}</Typography>
                            ) : null}
                        </Box>
                    ) : (
                        timeline.map((item) =>
                            item.kind === 'analysis' ? (
                                <Box key={item.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                        {translate('efChat.analysisSnapshot', { n: item.analysis.attemptNumber })}
                                    </Typography>
                                    {renderAnalysis(item.analysis)}
                                </Box>
                            ) : (
                                <EfChatMessage
                                    key={item.id}
                                    message={item.message}
                                    streamingLabel={translate('efChat.streaming')}
                                    errorLabel={translate('efChat.error')}
                                    errorTitleLabel={translate('efChat.errorTitle')}
                                    errorMessages={errorMessages}
                                    reasoningLabel={translate('efChat.reasoning')}
                                    attachmentDownloadLabel={translate('efChat.downloadAttachment')}
                                    onDownloadAttachment={handleDownloadAttachment}
                                />
                            )
                        )
                    )}
                    {footerSlot ? <Box sx={{ mt: 1 }}>{footerSlot}</Box> : null}
                    {inProgress && !chat.elicitation && !chat.messages.some((message) => message.status === 'streaming') ? (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: 'flex-start', pl: 0.5, py: 0.5 }}>
                            <CircularProgress size={12} thickness={5} />
                            <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>{translate('efChat.streaming')}</Typography>
                        </Stack>
                    ) : null}
                    {chat.elicitation ? (
                        <EfChatElicitationPrompt
                            questions={chat.elicitation.questions}
                            onAnswer={(answer) => {
                                void chat.resolveElicitation(answer);
                            }}
                            title={translate('efChat.elicitationTitle')}
                            sendLabel={translate('efChat.elicitationSend')}
                            placeholder={translate('efChat.elicitationPlaceholder')}
                            nextLabel={translate('efChat.elicitationNext')}
                            backLabel={translate('efChat.elicitationBack')}
                            progressLabel={(current, total) => translate('efChat.elicitationProgress', { current, total })}
                        />
                    ) : null}
                </Box>
                {approveButton ? <Box sx={{ display: 'flex', justifyContent: 'center' }}>{approveButton}</Box> : null}
                {attachError ? <Alert severity="error">{attachError}</Alert> : null}
                <EfChatComposer
                    onSend={handleComposerSend}
                    disabled={composerDisabled || sendingAttachments}
                    disabledReason={composerReasonText}
                    isStreaming={chat.isStreaming}
                    placeholder={evaluateMode ? translate('efChat.evaluatePlaceholder') : translate('efChat.placeholder')}
                    sendLabel={evaluateMode ? translate('efChat.startAnalysis') : translate('efChat.send')}
                    allowEmptySubmit={evaluateMode || pendingAttachments.length > 0}
                    leftSlot={leftSlot}
                    actionSlot={cancelButton}
                    attachmentSlot={timeline.length > 0 ? attachmentSlot : null}
                    onAttachFiles={handleAttachFiles}
                    pendingAttachments={pendingAttachments.map((attachment) => ({
                        id: attachment.id,
                        fileName: attachment.fileName,
                        fileSizeBytes: attachment.fileSizeBytes,
                        onRemove: () => removePendingAttachment(attachment.id)
                    }))}
                    attachmentsEnabled={attachmentsEnabled}
                    attachLabel={translate('efChat.attachComplementary')}
                    removeLabel={translate('efChat.removeAttachment')}
                    toolbarSlot={
                        <Stack direction="row" alignItems="center" spacing={1}>
                            {chat.contextUsage ? (
                                <EfContextUsageIndicator used={chat.contextUsage.used} limit={chat.contextUsage.limit} label={translate('efChat.contextUsage')} />
                            ) : null}
                            {isGlobalAdmin ? (
                                <EfModelSelector
                                    value={chat.model}
                                    models={chat.models}
                                    onChange={chat.setModel}
                                    label={translate('efChat.modelLabel')}
                                    disabled={composerDisabled}
                                />
                            ) : null}
                        </Stack>
                    }
                />
            </Stack>
            <ConfirmDialog
                open={deleteConfirmOpen}
                title={translate('efChat.deleteEfConfirmTitle')}
                message={translate('efChat.deleteEfConfirmMsg')}
                confirmLabel={translate('efChat.deleteEf')}
                confirmColor="error"
                onConfirm={confirmDeleteEf}
                onClose={() => setDeleteConfirmOpen(false)}
            />
        </Box>
    );
}
